/**
 * Module-level store behind `allPermissions()` / `allFamilies()`. Declarations register here as a
 * side effect of being imported (`./declarations/index.ts`, imported by the barrel), so any
 * importer sees the complete registry.
 *
 * Once `./declarations/index.ts` has loaded it calls `seal()`: from then on nothing can be
 * registered, so a permission cannot be declared (and thereby minted and made "registered") from
 * outside the package's own declaration files, whatever module path it is reached through
 * (red-team F-1, F-5).
 */
import { pathKey, pathProblem } from './path-rules';
import type { PermissionEntry, PermissionFamily } from './types';

const entries: PermissionEntry[] = [];
const byKey = new Map<string, PermissionEntry>();
/** Exact-case paths of every entry that carries a `permission` (tools, commands, data tables). */
const permissionPaths = new Set<string>();
const familyList: PermissionFamily[] = [];
let sealed = false;

function assertOpen(what: string): void {
  if (sealed) {
    throw new Error(
      `Security registry: cannot declare ${what} after the registry is sealed. ` +
        'Declare permissions in app/packages/security-registry/src/declarations/.',
    );
  }
}

/** Registers an entry; throws on a malformed, duplicate (case-insensitive) or orphaned path, or once sealed. */
export function register(entry: PermissionEntry): void {
  assertOpen(`"${entry.path}"`);
  const problem = pathProblem(entry.path);
  if (problem) throw new Error(`Security registry: path "${entry.path}" ${problem}.`);
  if (byKey.has(pathKey(entry.path))) {
    throw new Error(`Security registry: duplicate path "${entry.path}" (paths are unique case-insensitively).`);
  }
  if (entry.parent !== null && byKey.get(pathKey(entry.parent))?.path !== entry.parent) {
    throw new Error(`Security registry: parent "${entry.parent}" of "${entry.path}" is not declared.`);
  }
  const frozen = Object.freeze({ ...entry });
  entries.push(frozen);
  byKey.set(pathKey(frozen.path), frozen);
  if (frozen.permission !== undefined) permissionPaths.add(frozen.path);
}

/** The declared entry for `path`, compared case-insensitively. */
export const findEntry = (path: string): PermissionEntry | undefined =>
  typeof path === 'string' ? byKey.get(pathKey(path)) : undefined;

/** True when `path` (exact case) is the permission of a declared tool, command or data table. */
export const isDeclaredPermissionPath = (path: string): boolean => permissionPaths.has(path);

export function registerFamily(family: PermissionFamily): void {
  assertOpen(`family "${family.securityType}"`);
  if (familyList.some((f) => pathKey(f.securityType) === pathKey(family.securityType))) {
    throw new Error(`Security registry: duplicate family "${family.securityType}".`);
  }
  Object.freeze(family.universe);
  familyList.push(Object.freeze(family));
}

/** Closes the registry. Called once, by `./declarations/index.ts`, after every declaration has loaded. */
export function seal(): void {
  sealed = true;
}

/** Every declared entry, in declaration order. Data-driven family leaves are not listed (see `allFamilies`). */
export const allPermissions = (): readonly PermissionEntry[] => Object.freeze([...entries]);

export const allFamilies = (): readonly PermissionFamily[] => Object.freeze([...familyList]);
